import { Building2, User, Calendar, MapPin, Phone, Mail, Hash, FileText, ClipboardList, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const InvoiceDetails = ({
  companyInfo,
  customerInfo,
  invoiceInfo,
  isEditing,
  updateCompanyInfo,
  updateCustomerInfo,
  updateInvoiceInfo,
  agreements = [],
  onSelectAgreement
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* From Section */}
      <div className="invoice-section">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Invoice From
          </h3>
        </div>
        <div className="space-y-3">
          <InfoField
            label="Company"
            value={companyInfo.name}
            field="name"
            isEditing={isEditing}
            onChange={(value) => updateCompanyInfo("name", value)}
          />
          <InfoField
            label="Address"
            value={companyInfo.address}
            field="address"
            isEditing={isEditing}
            onChange={(value) => updateCompanyInfo("address", value)}
            icon={<MapPin className="w-3.5 h-3.5 text-muted-foreground" />}
          />
          <div className="text-sm text-muted-foreground">
            {isEditing ? (
              <div className="flex gap-2">
                <Input
                  value={companyInfo.city}
                  onChange={(e) => updateCompanyInfo("city", e.target.value)}
                  placeholder="City"
                  className="h-8 text-sm"
                />
                <Input
                  value={companyInfo.province}
                  onChange={(e) => updateCompanyInfo("province", e.target.value)}
                  placeholder="Province"
                  className="h-8 text-sm w-24"
                />
                <Input
                  value={companyInfo.postalCode}
                  onChange={(e) => updateCompanyInfo("postalCode", e.target.value)}
                  placeholder="Postal"
                  className="h-8 text-sm w-24"
                />
              </div>
            ) : (
              <span>{companyInfo.city}, {companyInfo.province} {companyInfo.postalCode}</span>
            )}
          </div>
          <InfoField
            label="Phone"
            value={companyInfo.phone}
            field="phone"
            isEditing={isEditing}
            onChange={(value) => updateCompanyInfo("phone", value)}
            icon={<Phone className="w-3.5 h-3.5 text-muted-foreground" />}
          />
          <InfoField
            label="Email"
            value={companyInfo.email}
            field="email"
            isEditing={isEditing}
            onChange={(value) => updateCompanyInfo("email", value)}
            icon={<Mail className="w-3.5 h-3.5 text-muted-foreground" />}
          />
          {/* HST Number */}
          <div className="pt-2 border-t border-border/50">
            <InfoField
              label="HST #"
              value={companyInfo.hstNumber}
              field="hstNumber"
              isEditing={isEditing}
              onChange={(value) => updateCompanyInfo("hstNumber", value)}
              icon={<Hash className="w-3.5 h-3.5 text-muted-foreground" />}
              placeholder="e.g., 123456789 RT0001"
            />
          </div>
        </div>
      </div>

      {/* To Section */}
      <div className="invoice-section">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-accent" />
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Invoice To
          </h3>
        </div>
        
        {/* Import from Rental Agreement Dropdown */}
        {isEditing && agreements.length > 0 && (
          <div className="mb-4 p-3 bg-accent/10 rounded-lg border border-accent/20">
            <Label className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
              <ClipboardList className="w-3 h-3" />
              Import from Rental Agreement
            </Label>
            <Select onValueChange={onSelectAgreement}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select an agreement..." />
              </SelectTrigger>
              <SelectContent>
                {agreements.map((agreement) => (
                  <SelectItem key={agreement.id} value={agreement.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{agreement.id}</span>
                      <span className="text-muted-foreground">-</span>
                      <span>{agreement.renterInfo?.name || 'No name'}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        <div className="space-y-3">
          <InfoField
            label="Customer"
            value={customerInfo.name}
            field="name"
            isEditing={isEditing}
            onChange={(value) => updateCustomerInfo("name", value)}
          />
          <InfoField
            label="Address"
            value={customerInfo.address}
            field="address"
            isEditing={isEditing}
            onChange={(value) => updateCustomerInfo("address", value)}
            icon={<MapPin className="w-3.5 h-3.5 text-muted-foreground" />}
          />
          <div className="text-sm text-muted-foreground">
            {isEditing ? (
              <div className="flex gap-2">
                <Input
                  value={customerInfo.city}
                  onChange={(e) => updateCustomerInfo("city", e.target.value)}
                  placeholder="City"
                  className="h-8 text-sm"
                />
                <Input
                  value={customerInfo.province}
                  onChange={(e) => updateCustomerInfo("province", e.target.value)}
                  placeholder="Province"
                  className="h-8 text-sm w-24"
                />
                <Input
                  value={customerInfo.postalCode}
                  onChange={(e) => updateCustomerInfo("postalCode", e.target.value)}
                  placeholder="Postal"
                  className="h-8 text-sm w-24"
                />
              </div>
            ) : (
              <span>{customerInfo.city}, {customerInfo.province} {customerInfo.postalCode}</span>
            )}
          </div>
          <InfoField
            label="Phone"
            value={customerInfo.phone}
            field="phone"
            isEditing={isEditing}
            onChange={(value) => updateCustomerInfo("phone", value)}
            icon={<Phone className="w-3.5 h-3.5 text-muted-foreground" />}
          />
          <InfoField
            label="Email"
            value={customerInfo.email}
            field="email"
            isEditing={isEditing}
            onChange={(value) => updateCustomerInfo("email", value)}
            icon={<Mail className="w-3.5 h-3.5 text-muted-foreground" />}
          />
          <InfoField
            label="Claim #"
            value={customerInfo.claimNumber}
            field="claimNumber"
            isEditing={isEditing}
            onChange={(value) => updateCustomerInfo("claimNumber", value)}
            icon={<FileText className="w-3.5 h-3.5 text-muted-foreground" />}
            placeholder="e.g., CLM-2024-001"
          />
          <InfoField
            label="Insurance Company"
            value={customerInfo.insuranceCompany}
            field="insuranceCompany"
            isEditing={isEditing}
            onChange={(value) => updateCustomerInfo("insuranceCompany", value)}
            icon={<Shield className="w-3.5 h-3.5 text-muted-foreground" />}
            placeholder="e.g., State Farm"
          />
          <InfoField
            label="Policy #"
            value={customerInfo.policyNumber}
            field="policyNumber"
            isEditing={isEditing}
            onChange={(value) => updateCustomerInfo("policyNumber", value)}
            icon={<Hash className="w-3.5 h-3.5 text-muted-foreground" />}
            placeholder="e.g., POL-123456"
          />
        </div>
      </div>

      {/* Invoice Meta */}
      <div className="md:col-span-2">
        <div className="flex flex-wrap gap-6 p-4 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <div>
              <Label className="text-xs text-muted-foreground uppercase">Invoice Date</Label>
              {isEditing ? (
                <Input
                  type="date"
                  value={invoiceInfo.date}
                  onChange={(e) => updateInvoiceInfo("date", e.target.value)}
                  className="h-8 text-sm w-36 mt-1"
                />
              ) : (
                <p className="text-sm font-medium text-foreground">
                  {new Date(invoiceInfo.date).toLocaleDateString('en-CA', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <div>
              <Label className="text-xs text-muted-foreground uppercase">Due Date</Label>
              {isEditing ? (
                <Input
                  type="date"
                  value={invoiceInfo.dueDate}
                  onChange={(e) => updateInvoiceInfo("dueDate", e.target.value)}
                  className="h-8 text-sm w-36 mt-1"
                />
              ) : (
                <p className="text-sm font-medium text-foreground">
                  {new Date(invoiceInfo.dueDate).toLocaleDateString('en-CA', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex items-center gap-3">
              <div>
                <Label className="text-xs text-muted-foreground uppercase">Status</Label>
                <Select
                  value={invoiceInfo.status}
                  onValueChange={(value) => updateInvoiceInfo("status", value)}
                >
                  <SelectTrigger className="h-8 w-32 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value, field, isEditing, onChange, icon, placeholder }) => {
  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        {icon}
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 text-sm"
          placeholder={placeholder || label}
        />
      </div>
    );
  }

  // Don't render empty non-name fields in view mode
  if (!value && field !== 'name') {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      {icon}
      <span className={field === 'name' ? 'font-medium text-foreground' : 'text-muted-foreground'}>
        {value || (field === 'name' ? 'Unnamed' : '')}
      </span>
    </div>
  );
};
